import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useAPIProvider } from '../../../../../common/hooks/use-api-provider';
import { useQuestionnaire } from '../../../../../modernization-common/side-panel/hooks/use-questionnaire';
import { annotationLabelsAtom, annotationMetadataAtom } from '../../../atoms/annotation-atoms';
import type { DynamicAnswerEntryT } from '../../../types/frame-timeline-v4';
type AttributeOptionsT = {
  options: string[];
  isLoading: boolean;
  /**
   * An existing answer entry for this question, reused as a template when writing a new value so
   * the question text and dynamic-field tags are preserved.
   */
  answerTemplate: DynamicAnswerEntryT | undefined;
};

/** Resolves an attribute's possible values by walking annotation → label → questionnaire question. */
export const useAttributeOptions = (annotationId: string, questionId: string): AttributeOptionsT => {
  const {
    fetchQuestionnaire
  } = useAPIProvider();
  const metadata = useAtomValue(annotationMetadataAtom).get(annotationId);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const questionnaireId = useMemo(() => {
    const label = annotationLabels.find(candidate => candidate.title === metadata?.labelId);
    return label?.questionnaire?.[0]?.questionnaireId ?? '';
  }, [annotationLabels, metadata?.labelId]);
  const {
    questionnaire,
    isLoading
  } = useQuestionnaire({
    fetchQuestionnaire,
    questionnaireId
  });
  const options = useMemo(() => {
    const answerOptions = questionnaire?.questions?.[questionId]?.answers ?? [];
    return answerOptions.map(option => option.answer).filter((answer): answer is string => typeof answer === 'string');
  }, [questionnaire, questionId]);
  const answerTemplate = useMemo((): DynamicAnswerEntryT | undefined => {
    const entry = metadata?.dynamicAnswers?.find(segment => segment.answers[questionId])?.answers[questionId];
    if (!entry) {
      return undefined;
    }
    return {
      answers: entry.answers.filter((answer): answer is string => typeof answer === 'string'),
      question: entry.question,
      tags: entry.tags
    };
  }, [metadata?.dynamicAnswers, questionId]);
  return {
    options,
    isLoading,
    answerTemplate
  };
};